const EVENT_PROGRESS_ORDER = [
    'CASE_CREATED',
    'DAMAGE_PHOTO_REQUESTED',
    'DAMAGE_PHOTO_SUBMITTED',
    'REPLACEMENT_TITLE_REQUESTED',
    'REPLACEMENT_TITLE_SUBMITTED',
    'REPLACEMENT_TITLE_APPROVED',
    'REPLACEMENT_DAMAGE_CASE_CREATED',
    'REPLACEMENT_DAMAGE_PHOTO_REQUESTED',
    'REPLACEMENT_DAMAGE_PHOTO_SUBMITTED',
    'REPLACEMENT_TITLE_REQUESTED_BONUS',
    'REPLACEMENT_TITLE_SUBMITTED_BONUS',
    'REPLACEMENT_TITLE_APPROVED_BONUS',
    'REPLACEMENT_DAMAGE_CASE_CREATED_BONUS',
    'REPLACEMENT_DAMAGE_PHOTO_REQUESTED_BONUS',
    'REPLACEMENT_DAMAGE_PHOTO_SUBMITTED_BONUS',
    'REFUND_ISSUED',
];

const PURCHASE_WAIT_MS = 3 * 24 * 60 * 60 * 1000;
const CASE_WAIT_MS = 16 * 60 * 60 * 1000;
const REPLACEMENT_APPROVAL_WAIT_MS = 6 * 24 * 60 * 60 * 1000;
const TICKET_EVENT_TYPES = [
    'CASE_CREATED',
    'REPLACEMENT_DAMAGE_CASE_CREATED',
    'REPLACEMENT_DAMAGE_CASE_CREATED_BONUS',
];

export const AUTOMATION_EVENT_LABELS = {
    CASE_CREATED: 'Case Created',
    REPLACEMENT_DAMAGE_CASE_CREATED: 'Replacement Damage Case Created',
    REPLACEMENT_DAMAGE_CASE_CREATED_BONUS: 'Replacement Damage Case Created Bonus',
};

export const AUTOMATION_REASON_LABELS = {
    NO_CASE_CREATED: 'No Case Created event',
    PURCHASE_WAITING_3_DAYS: 'Wait 3 days after purchase',
    PURCHASE_DATE_MISSING: 'Add a valid purchase date to this order',
    CASE_CREATED_WAIT_EXPIRED: 'Case Created is at least 16 hours old',
    REPLACEMENT_TITLE_APPROVED_WAIT_EXPIRED: 'Replacement Title Approved is at least 6 days old',
    REPLACEMENT_DAMAGE_CASE_WAIT_EXPIRED: 'Replacement Damage Case Created is at least 16 hours old',
    CASE_CREATED_WAITING_16_HOURS: 'Wait 16 hours after Case Created',
    REPLACEMENT_TITLE_APPROVED_WAITING_6_DAYS: 'Replacement Title Approved is less than 6 days old',
    REPLACEMENT_DAMAGE_CASE_WAITING_16_HOURS: 'Wait 16 hours after Replacement Damage Case Created',
    REPLACEMENT_TITLE_MISSING: 'Approved replacement title is missing',
    NOT_AT_AUTOMATION_STAGE: 'Item is not currently at an automation stage',
};

const parseEventDate = (eventDateText, defaultYear) => {
    if (!eventDateText) return null;
    const match = eventDateText.trim().match(
        /^(\d{1,2})\.(\d{1,2})(?:\.(\d{4}))?\.?(?:\s+(\d{1,2}):(\d{2}))?$/
    );
    if (!match) return null;

    const date = new Date(
        Number(match[3] || defaultYear),
        Number(match[2]) - 1,
        Number(match[1]),
        Number(match[4] || 0),
        Number(match[5] || 0),
        0,
        0
    );
    return Number.isNaN(date.getTime())
        || date.getDate() !== Number(match[1])
        || date.getMonth() !== Number(match[2]) - 1
        || date.getHours() !== Number(match[4] || 0)
        || date.getMinutes() !== Number(match[5] || 0)
        ? null : date;
};

const effectiveEventDate = (event, now) => {
    if (TICKET_EVENT_TYPES.includes(event?.type) && event?.ticketEntriesText) {
        try {
            const entries = JSON.parse(event.ticketEntriesText);
            if (Array.isArray(entries)) {
                for (let index = entries.length - 1; index >= 0; index -= 1) {
                    const parsed = parseEventDate(entries[index]?.eventDateText, now.getFullYear());
                    if (parsed) return parsed;
                }
            }
        } catch (error) {
            // Legacy events use the top-level eventDateText below.
        }
    }
    return parseEventDate(event?.eventDateText, now.getFullYear());
};

const isOldEnough = (event, now, thresholdMs) => {
    const eventDate = effectiveEventDate(event, now);
    if (!eventDate || eventDate.getTime() > now.getTime()) return false;
    return now.getTime() - eventDate.getTime() >= thresholdMs;
};

const eligible = (targetEventType, reason, automationTitle) => ({
    eligible: true,
    targetEventType,
    reason,
    automationTitle,
});

const skipped = (reason) => ({
    eligible: false,
    targetEventType: null,
    reason,
    automationTitle: null,
});

const waiting = (reason, date, wait) => ({
    ...skipped(reason),
    availableAt: date ? new Date(date.getTime() + wait).toISOString() : null,
});

export const evaluateAutomationItem = (item, currentDate = new Date(), order = item?.wobOrder) => {
    const createdAt = order?.createdAt ? new Date(order.createdAt) : null;
    let purchaseDate = parseEventDate(
        order?.purchaseDateText,
        createdAt && !Number.isNaN(createdAt.getTime()) ? createdAt.getFullYear() : currentDate.getFullYear()
    );
    if (!purchaseDate) return skipped('PURCHASE_DATE_MISSING');
    if (!order.purchaseDateText.includes(':') && createdAt
        && purchaseDate.toDateString() === createdAt.toDateString()) {
        purchaseDate = createdAt;
    }
    if (currentDate.getTime() - purchaseDate.getTime() < PURCHASE_WAIT_MS) {
        return waiting('PURCHASE_WAITING_3_DAYS', purchaseDate, PURCHASE_WAIT_MS);
    }
    const events = Array.isArray(item?.events) ? item.events : [];
    const currentEvent = EVENT_PROGRESS_ORDER.reduce((latest, eventType) => (
        events.find(event => event.type === eventType) || latest
    ), null);
    const hasCaseCreated = events.some(event => event.type === 'CASE_CREATED');
    const isBonusApproved = currentEvent?.type === 'REPLACEMENT_TITLE_APPROVED_BONUS';
    const isApproved = currentEvent?.type === 'REPLACEMENT_TITLE_APPROVED' || isBonusApproved;
    const isBonusDamageCase = currentEvent?.type === 'REPLACEMENT_DAMAGE_CASE_CREATED_BONUS';
    const isDamageCase = currentEvent?.type === 'REPLACEMENT_DAMAGE_CASE_CREATED' || isBonusDamageCase;
    const approvedType = isBonusApproved || isBonusDamageCase
        ? 'REPLACEMENT_TITLE_APPROVED_BONUS'
        : 'REPLACEMENT_TITLE_APPROVED';
    const replacementTitle = events.find(
        event => event.type === approvedType
    )?.replacementTitle?.trim() || null;

    if (isApproved) {
        if (!isOldEnough(currentEvent, currentDate, REPLACEMENT_APPROVAL_WAIT_MS)) {
            return waiting('REPLACEMENT_TITLE_APPROVED_WAITING_6_DAYS', effectiveEventDate(currentEvent, currentDate), REPLACEMENT_APPROVAL_WAIT_MS);
        }
        return replacementTitle
            ? eligible(
                isBonusApproved
                    ? 'REPLACEMENT_DAMAGE_CASE_CREATED_BONUS'
                    : 'REPLACEMENT_DAMAGE_CASE_CREATED',
                'REPLACEMENT_TITLE_APPROVED_WAIT_EXPIRED',
                replacementTitle
            )
            : skipped('REPLACEMENT_TITLE_MISSING');
    }
    if (isDamageCase) {
        if (!isOldEnough(currentEvent, currentDate, CASE_WAIT_MS)) {
            return waiting('REPLACEMENT_DAMAGE_CASE_WAITING_16_HOURS', effectiveEventDate(currentEvent, currentDate), CASE_WAIT_MS);
        }
        return replacementTitle
            ? eligible(
                currentEvent.type,
                'REPLACEMENT_DAMAGE_CASE_WAIT_EXPIRED',
                replacementTitle
            )
            : skipped('REPLACEMENT_TITLE_MISSING');
    }
    if (currentEvent?.type === 'CASE_CREATED') {
        return isOldEnough(currentEvent, currentDate, CASE_WAIT_MS)
            ? eligible('CASE_CREATED', 'CASE_CREATED_WAIT_EXPIRED', item?.title)
            : waiting('CASE_CREATED_WAITING_16_HOURS', effectiveEventDate(currentEvent, currentDate), CASE_WAIT_MS);
    }
    if (!hasCaseCreated) {
        return eligible('CASE_CREATED', 'NO_CASE_CREATED', item?.title);
    }
    return skipped('NOT_AT_AUTOMATION_STAGE');
};
